/**
 * Copyright Highway9 Networks Inc.
 *
 * Binds the CSP app to the shared assistant contract (MCS-4480).
 *
 * Value handed to AiAssistantProvider in App.tsx. Maps Redux aiAssistantSlice,
 * REST/WS clients, identity, and optional CSP services (prompts / tasks)
 * onto AiAssistantAdapter so ChatPanel stays host-agnostic.
 * Dream recommendations are paused (Alice flow broken) — not wired.
 */

import type {
  AiAssistantAdapter,
  AiAssistantIdentity,
  AiAssistantPromptLibrary,
  AiAssistantRestClient,
  AiAssistantScheduledTasks,
  AiAssistantSocketHandlers,
  AiAssistantSocketLike,
  AiAssistantState,
  AiAssistantStore,
  AssistantPrompt,
  AssistantPromptCategory,
} from "@h9/ui-lib/components/organisms/AiAssistant";
import { bindActionCreators } from "@reduxjs/toolkit";
import { useMemo } from "react";

import { store, type RootState } from "~/store";
import { useAppSelector } from "~/store/hooks";
import { aiAssistantActions } from "~/store/context/aiAssistantSlice";
import { selectTimeZone } from "~/store/context/utilitySlice";
import {
  AiAssistantSocket,
  aiAssistantRest,
  currentAssistantUserRef,
} from "~/services/ai-assistant-service";
import { authenticationService } from "~/services/authentication-service";
import { userService } from "~/services/user-service";
import { getImageSrc } from "~/services/image-services";
import { zivaPromptService } from "~/services/ziva-prompt-service";
import { zivaPromptCategoryService } from "~/services/ziva-prompt-category-service";
import { scheduledTaskService, type ScheduledTask } from "~/services/scheduled-task-service";

/** Browser-facing base; the Go bridge registers the same value as its PublicPrefix. */
const CSP_ASSISTANT_API_BASE = "/api/v1/saas/ai-assistant";

/**
 * Narrows the app store to the assistant slice. A hook rather than a bound closure so
 * react-redux keeps its per-value subscription and a streaming turn does not re-render
 * consumers that did not select the part that changed.
 */
function useAssistantSelector<T>(
  selector: (state: AiAssistantState) => T,
  isEqual?: (a: T, b: T) => boolean
): T {
  return useAppSelector(
    (root: RootState) => selector(root.aiAssistant),
    isEqual
  );
}

/**
 * Bind all slice actions to dispatch at module scope.
 * bindActionCreators wraps each action creator so callers don't touch dispatch directly.
 * Stable reference (module-level) ensures the adapter object identity is unchanged on re-renders.
 */
const actions = bindActionCreators(aiAssistantActions, store.dispatch);

const assistantStore: AiAssistantStore = {
  useSelector: useAssistantSelector,
  getState: () => store.getState().aiAssistant,
  actions,
};

const rest: AiAssistantRestClient = aiAssistantRest;

/** The CSP socket appends the JWT as a query parameter; browsers cannot set WS headers. */
const createSocket = (handlers: AiAssistantSocketHandlers): AiAssistantSocketLike =>
  new AiAssistantSocket(handlers);

const promptLibrary: AiAssistantPromptLibrary = {
  list: () => zivaPromptService.list(),
  get: (id) => zivaPromptService.get(id),
  create: (payload) => zivaPromptService.create(payload as AssistantPrompt),
  update: (id, payload) => zivaPromptService.update(id, payload as AssistantPrompt),
  remove: (id) => zivaPromptService.remove(id),
  removeVersion: (id, versionId) => zivaPromptService.removeVersion(id, versionId),
  categories: {
    list: () => zivaPromptCategoryService.list(),
    create: (payload) => zivaPromptCategoryService.create(payload as AssistantPromptCategory),
    update: (id, payload) =>
      zivaPromptCategoryService.update(id, payload as AssistantPromptCategory),
    remove: (id) => zivaPromptCategoryService.remove(id),
  },
};

const scheduledTasks: AiAssistantScheduledTasks = {
  list: () => scheduledTaskService.list(),
  create: (payload) => scheduledTaskService.create(payload as ScheduledTask),
  update: (id, payload) => scheduledTaskService.update(id, payload as ScheduledTask),
  remove: (id) => scheduledTaskService.remove(id),
};

/**
 * The CSP adapter. Memoized on the few values that actually change, because every
 * consumer re-renders when this object's identity does.
 */
export function useSaasAiAssistantAdapter(): AiAssistantAdapter {
  const timeZone = useAppSelector(selectTimeZone);
  const email = authenticationService.currentUserValue?.email;
  const userId = authenticationService.currentUserValue?.userId;
  const isOperator = Boolean(authenticationService.isOperator());

  const identity: AiAssistantIdentity = useMemo(
    () => ({
      email,
      currentUser: currentAssistantUserRef(),
      userId,
      isOperator,
      getUserImage: getImageSrc,
      listUsers: () => userService.getUsers(),
    }),
    [email, userId, isOperator]
  );

  return useMemo(
    () => ({
      config: {
        assistantName: "Ziva",
        apiBasePath: CSP_ASSISTANT_API_BASE,
        timeZone,
      },
      store: assistantStore,
      rest,
      createSocket,
      identity,
      promptLibrary,
      scheduledTasks,
      // recommendations omitted while dream/Alice flow is paused
    }),
    [timeZone, identity]
  );
}
